/**
 * The Kids Typing Journey curriculum — 15 levels of progressive lessons.
 * ---------------------------------------------------------------------------
 * Design rules, applied consistently so the difficulty curve is honest:
 *
 *  1. NEVER show a key the child has not been taught. Every level carries a
 *     cumulative key set and the content generator filters against it.
 *  2. Two new keys at a time, maximum. Each pair gets a drill, a mix-with-
 *     everything-so-far lesson, and a "prove it" lesson.
 *  3. Beginner levels are STRICT — a wrong key does not advance. You cannot
 *     build muscle memory while running ahead of your fingers. Strictness is
 *     dropped at Level 11, where fluency matters more than perfection.
 *  4. Star targets rise slowly and are always reachable: 1★ = you finished,
 *     2★ = accurate, 3★ = accurate AND at the level's pace.
 *
 * IDs are permanent. They are the keys in a child's saved progress, so a
 * lesson may be re-titled or re-tuned but never renumbered.
 */
import type { LessonDef, LevelDef, LessonKind } from '../types';

const HOME_ROW = ['a', 's', 'd', 'f', 'j', 'k', 'l', ';'];
const TOP_ROW = ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'];
const BOTTOM_ROW = ['z', 'x', 'c', 'v', 'b', 'n', 'm'];
const NUMBERS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
const SYMBOLS = [
  '.',
  ',',
  "'",
  '"',
  '!',
  '?',
  '-',
  '@',
  '#',
  '$',
  '%',
  '&',
  '+',
  '=',
  '(',
  ')',
  ':',
  '/',
];

interface LessonSeed {
  title: string;
  kind: LessonKind;
  /** Defaults to the level's new keys. */
  focusKeys?: string[];
  targetWpm: number;
  targetAccuracy: number;
  length: number;
  durationSec?: number;
  coach: string;
  /** Overrides the level default. */
  strict?: boolean;
}

interface LevelSeed {
  slug: string;
  /** <=62 chars. Kept separate from `title` — one is a SERP headline, the
      other is what a child sees on the map. */
  seoTitle: string;
  /** 90–165 chars, written as ad copy for a parent scanning search results. */
  seoDescription: string;
  title: string;
  subtitle: string;
  emoji: string;
  color: string;
  newKeys: string[];
  description: string;
  quickAnswer: string;
  strict: boolean;
  lessons: LessonSeed[];
}

const LEVEL_SEEDS: LevelSeed[] = [
  /* ────────────────────────────────────────────────────────────── Level 1 */
  {
    slug: 'home-base-f-and-j',
    seoTitle: 'Home Row F and J — Typing Lesson for Kids | TypeSwift',
    seoDescription:
      'Learn the F and J home keys — the two keys with raised bumps that anchor touch typing. Three free lessons for kids with an animated keyboard and finger guide.',
    title: 'Home Base',
    subtitle: 'F and J',
    emoji: '🏠',
    color: '--k-mint',
    newKeys: ['f', 'j'],
    description:
      'Everything starts with F and J. These two keys have tiny raised bumps you can feel without looking, so your pointer fingers always know where home is. In this level your hands learn to find home base and come back to it after every single press.',
    quickAnswer:
      'F and J are the home-row anchor keys. They have raised bumps so touch typists can find the correct hand position without looking. The left pointer finger rests on F and the right pointer finger rests on J.',
    strict: true,
    lessons: [
      {
        title: 'Find the bumps',
        kind: 'keys',
        targetWpm: 8,
        targetAccuracy: 90,
        length: 60,
        coach:
          'Feel for the two little bumps. Left pointer on F, right pointer on J. Never look down!',
      },
      {
        title: 'F and J together',
        kind: 'keys',
        targetWpm: 10,
        targetAccuracy: 92,
        length: 90,
        coach: 'Alternate hands. Press, then bring the finger straight back to its bump.',
      },
      {
        title: 'Home base check',
        kind: 'boss',
        targetWpm: 12,
        targetAccuracy: 94,
        length: 110,
        coach: 'No hints this time. Trust your fingers.',
      },
    ],
  },
  /* ────────────────────────────────────────────────────────────── Level 2 */
  {
    slug: 'tall-finger-d-and-k',
    seoTitle: 'D and K Keys — Home Row Typing for Kids | TypeSwift',
    seoDescription:
      'Teach a child the D and K home-row keys with their middle fingers. Three free typing lessons with an animated keyboard, hand guide and instant feedback.',
    title: 'Tall Towers',
    subtitle: 'D and K',
    emoji: '🗼',
    color: '--k-sky',
    newKeys: ['d', 'k'],
    description:
      'Your tall middle fingers get their own keys: D on the left and K on the right. They sit right next to F and J, so your hands barely move. Learn them, then mix them with home base.',
    quickAnswer:
      'D is typed with the left middle finger and K with the right middle finger. Both sit on the home row directly beside the F and J anchor keys.',
    strict: true,
    lessons: [
      {
        title: 'Meet D and K',
        kind: 'keys',
        targetWpm: 10,
        targetAccuracy: 90,
        length: 70,
        coach: 'Middle fingers only. Everything else stays resting on home base.',
      },
      {
        title: 'Mix with F and J',
        kind: 'keys',
        focusKeys: ['f', 'j', 'd', 'k'],
        targetWpm: 12,
        targetAccuracy: 92,
        length: 100,
        coach: 'Four keys now. Slow is smooth, and smooth is fast.',
      },
      {
        title: 'Tower challenge',
        kind: 'boss',
        focusKeys: ['f', 'j', 'd', 'k'],
        targetWpm: 14,
        targetAccuracy: 94,
        length: 120,
        coach: 'Eyes up. You already know where they are.',
      },
    ],
  },
  /* ────────────────────────────────────────────────────────────── Level 3 */
  {
    slug: 'ring-finger-s-and-l',
    seoTitle: 'S and L Keys — Ring Finger Typing for Kids | TypeSwift',
    seoDescription:
      'The S and L keys train the ring fingers, the weakest pair on the keyboard. Three free lessons for kids with finger-by-finger guidance and instant feedback.',
    title: 'Ring Road',
    subtitle: 'S and L',
    emoji: '💍',
    color: '--k-lagoon',
    newKeys: ['s', 'l'],
    description:
      'Ring fingers are the sleepy ones — they need the most practice. S belongs to your left ring finger and L to your right. Wake them up here and the rest of the keyboard gets much easier.',
    quickAnswer:
      'S is typed with the left ring finger and L with the right ring finger. Ring fingers are the weakest, so they need the most deliberate practice.',
    strict: true,
    lessons: [
      {
        title: 'Wake the ring fingers',
        kind: 'keys',
        targetWpm: 11,
        targetAccuracy: 90,
        length: 70,
        coach: 'Ring fingers are shy. Press gently and let the other fingers stay still.',
      },
      {
        title: 'Six keys',
        kind: 'keys',
        focusKeys: ['f', 'j', 'd', 'k', 's', 'l'],
        targetWpm: 13,
        targetAccuracy: 92,
        length: 110,
        coach: 'Six keys under six fingers. Keep them all touching home.',
      },
      {
        title: 'Ring road race',
        kind: 'boss',
        focusKeys: ['f', 'j', 'd', 'k', 's', 'l'],
        targetWpm: 15,
        targetAccuracy: 94,
        length: 130,
        coach: 'Last one before the pinkies join in!',
      },
    ],
  },
  /* ────────────────────────────────────────────────────────────── Level 4 */
  {
    slug: 'pinky-power-a-and-semicolon',
    seoTitle: 'A and Semicolon Keys — Typing for Kids | TypeSwift',
    seoDescription:
      'Finish the home row with A and the semicolon, typed with the little fingers. Three free typing lessons for kids with an animated keyboard and hand guide.',
    title: 'Pinky Power',
    subtitle: 'A and ;',
    emoji: '🤙',
    color: '--k-grape',
    newKeys: ['a', ';'],
    description:
      'The last two resting keys belong to your smallest fingers. A is the left pinky and the semicolon is the right pinky. Once you own these, all eight fingers have a home of their own.',
    quickAnswer:
      'A is typed with the left pinky and the semicolon key with the right pinky. Together with S, D, F, J, K and L they complete the eight home-row resting positions.',
    strict: true,
    lessons: [
      {
        title: 'Little finger, big job',
        kind: 'keys',
        targetWpm: 11,
        targetAccuracy: 88,
        length: 70,
        coach:
          'Pinkies are small but strong. Keep your wrist still and just reach with the finger.',
      },
      {
        title: 'The whole home row',
        kind: 'keys',
        focusKeys: HOME_ROW,
        targetWpm: 14,
        targetAccuracy: 92,
        length: 130,
        coach: 'All eight fingers, all eight keys. This is the foundation of everything.',
      },
      {
        title: 'Home row master',
        kind: 'boss',
        focusKeys: HOME_ROW,
        targetWpm: 16,
        targetAccuracy: 94,
        length: 150,
        coach: 'Prove the home row is yours.',
      },
    ],
  },
  /* ────────────────────────────────────────────────────────────── Level 5 */
  {
    slug: 'space-bar',
    seoTitle: 'Space Bar Typing Lesson for Kids | TypeSwift',
    seoDescription:
      'Learn to press the space bar with one thumb while all eight fingers stay on the home row. Three free typing lessons for kids on rhythm and word spacing.',
    title: 'The Big Bar',
    subtitle: 'Space',
    emoji: '🚀',
    color: '--k-tangerine',
    newKeys: [' '],
    description:
      'The space bar is the widest key and the most used one of all. It gets a whole level to itself because the habit you build now — one relaxed thumb, never both — will follow you for life.',
    quickAnswer:
      'The space bar is pressed with the thumb, usually the thumb of your dominant hand, while all eight fingers stay resting on the home row. It is the most frequently pressed key on a keyboard.',
    strict: true,
    lessons: [
      {
        title: 'Thumb taps',
        kind: 'keys',
        focusKeys: [...HOME_ROW, ' '],
        targetWpm: 14,
        targetAccuracy: 92,
        length: 110,
        coach: 'Use one thumb only. Let it hover over the middle of the bar.',
      },
      {
        title: 'Chunks and gaps',
        kind: 'keys',
        focusKeys: [...HOME_ROW, ' '],
        targetWpm: 16,
        targetAccuracy: 93,
        length: 140,
        coach: 'Space is part of the rhythm, not a pause. Keep the beat steady.',
      },
      {
        title: 'Space race',
        kind: 'boss',
        focusKeys: [...HOME_ROW, ' '],
        targetWpm: 18,
        targetAccuracy: 94,
        length: 160,
        coach: 'Fingers home, thumb ready. Go!',
      },
    ],
  },
  /* ────────────────────────────────────────────────────────────── Level 6 */
  {
    slug: 'home-row-words',
    seoTitle: 'Home Row Words — Typing Lessons for Kids | TypeSwift',
    seoDescription:
      'Add G and H, then type real words without leaving the home row: ask, glad, flash, salad. Five free typing lessons for kids with an animated finger guide.',
    title: 'First Words',
    subtitle: 'G, H and home-row words',
    emoji: '📖',
    color: '--k-mango',
    // G and H are the two inward index-finger reaches that complete the home
    // row. They belong here rather than with the top or bottom row: without
    // them a child cannot spell "glad", "half" or "flash", and this is the
    // level where letters first become words.
    newKeys: ['g', 'h'],
    description:
      'Your pointer fingers learn one more move each — a small reach inward to G and H — and then the letters start making real words. Dad. Glass. Flash. Salad. Everything here is typed without your hands leaving the home row.',
    quickAnswer:
      'Home-row words are real English words spelled only with the letters A, S, D, F, G, H, J, K and L — for example "ask", "glad", "flash" and "salad". G and H are reached by stretching the index fingers inward from F and J, which completes the home row and makes real words possible.',
    strict: true,
    lessons: [
      {
        title: 'Reach for G and H',
        kind: 'keys',
        targetWpm: 13,
        targetAccuracy: 90,
        length: 90,
        coach:
          'Stretch your pointer fingers sideways — F reaches to G, J reaches to H. Then straight back home.',
      },
      {
        title: 'Tiny words',
        kind: 'words',
        focusKeys: [...HOME_ROW, 'g', 'h', ' '],
        targetWpm: 16,
        targetAccuracy: 93,
        length: 120,
        coach: 'Read the whole word first, then type it in one flow.',
      },
      {
        title: 'Longer words',
        kind: 'words',
        focusKeys: [...HOME_ROW, 'g', 'h', ' '],
        targetWpm: 18,
        targetAccuracy: 94,
        length: 150,
        coach: 'Do not spell it out letter by letter — let your hand type the shape.',
      },
      {
        title: 'Word sprint',
        kind: 'timed',
        focusKeys: [...HOME_ROW, 'g', 'h', ' '],
        targetWpm: 20,
        targetAccuracy: 93,
        length: 200,
        durationSec: 60,
        coach: 'One minute. How many words can you land cleanly?',
      },
      {
        title: 'First words champion',
        kind: 'boss',
        focusKeys: [...HOME_ROW, 'g', 'h', ' '],
        targetWpm: 20,
        targetAccuracy: 95,
        length: 170,
        coach: 'You are about to leave the home row. Show it who is boss first.',
      },
    ],
  },
  /* ────────────────────────────────────────────────────────────── Level 7 */
  {
    slug: 'top-row-typing',
    seoTitle: 'Top Row Typing — QWERTY Lessons for Kids | TypeSwift',
    seoDescription:
      'Learn the top row Q W E R T Y U I O P two keys at a time, reaching up from the home row and back. Six free typing lessons for kids with a hand guide.',
    title: 'Sky Row',
    subtitle: 'Q W E R T Y U I O P',
    emoji: '☁️',
    color: '--k-sky',
    newKeys: TOP_ROW,
    description:
      'Time to reach up. Every finger stretches one row higher and then comes straight back down to home. Ten new keys arrive here — two at a time — and suddenly most English words are within reach.',
    quickAnswer:
      'The top row (Q W E R T Y U I O P) is typed by reaching each finger up one row from the home row and immediately returning. The left hand covers Q W E R T and the right hand covers Y U I O P.',
    strict: true,
    lessons: [
      {
        title: 'E and I',
        kind: 'keys',
        focusKeys: ['e', 'i'],
        targetWpm: 16,
        targetAccuracy: 90,
        length: 90,
        coach: 'Middle fingers reach up. D goes to E, K goes to I. Then straight back down.',
      },
      {
        title: 'R and U',
        kind: 'keys',
        focusKeys: ['r', 'u'],
        targetWpm: 16,
        targetAccuracy: 90,
        length: 90,
        coach: 'Pointer fingers reach up from F and J.',
      },
      {
        title: 'W and O',
        kind: 'keys',
        focusKeys: ['w', 'o'],
        targetWpm: 16,
        targetAccuracy: 90,
        length: 90,
        coach: 'Ring fingers up. Small movement, big control.',
      },
      {
        title: 'Q, P, T and Y',
        kind: 'keys',
        focusKeys: ['q', 'p', 't', 'y'],
        targetWpm: 16,
        targetAccuracy: 89,
        length: 100,
        coach: 'The far corners. Pinkies get Q and P; pointers stretch inward for T and Y.',
      },
      {
        title: 'Sky row words',
        kind: 'words',
        targetWpm: 20,
        targetAccuracy: 93,
        length: 170,
        coach: 'Real words using both rows now. Reach up, come home.',
      },
      {
        title: 'Sky row champion',
        kind: 'boss',
        targetWpm: 22,
        targetAccuracy: 94,
        length: 190,
        coach: 'Two rows down, one to go.',
      },
    ],
  },
  /* ────────────────────────────────────────────────────────────── Level 8 */
  {
    slug: 'bottom-row-typing',
    seoTitle: 'Bottom Row Typing — Z X C V B N M for Kids | TypeSwift',
    seoDescription:
      'Master the bottom row Z X C V B N M plus the comma and full stop. Five free typing lessons that complete the alphabet, with an animated keyboard for kids.',
    title: 'Cave Row',
    subtitle: 'Z X C V B N M',
    emoji: '🕳️',
    color: '--k-grape',
    // The comma and full stop physically live on the bottom row, under the
    // right middle and ring fingers, so they are taught with their neighbours
    // rather than saved for the punctuation level.
    newKeys: [...BOTTOM_ROW, ',', '.'],
    description:
      'The last row of letters lives below home, along with the comma and the full stop. Reaching down is the trickiest move on the keyboard, so we take it slowly. Finish this level and you can type every letter of the alphabet.',
    quickAnswer:
      'The bottom row (Z X C V B N M, plus the comma and full stop) is typed by curling each finger down one row from the home row. Completing it means a typist can reach all 26 letters without looking at the keyboard.',
    strict: true,
    lessons: [
      {
        title: 'C and comma',
        kind: 'keys',
        focusKeys: ['c', ','],
        targetWpm: 16,
        targetAccuracy: 89,
        length: 90,
        coach: 'Middle fingers curl down. Keep the other fingers on home.',
      },
      {
        title: 'V, B, N and M',
        kind: 'keys',
        focusKeys: ['v', 'b', 'n', 'm'],
        targetWpm: 16,
        targetAccuracy: 89,
        length: 100,
        coach: 'Pointer fingers do the most work down here. B is left, N is right.',
      },
      {
        title: 'X, Z and full stop',
        kind: 'keys',
        focusKeys: ['x', 'z', '.'],
        targetWpm: 15,
        targetAccuracy: 88,
        length: 90,
        coach: 'Ring and pinky reach down. These are rare letters, so be patient.',
      },
      {
        title: 'The whole alphabet',
        kind: 'words',
        targetWpm: 22,
        targetAccuracy: 93,
        length: 190,
        coach: 'Every letter is now yours. Type real words with all three rows.',
      },
      {
        title: 'Alphabet champion',
        kind: 'boss',
        targetWpm: 24,
        targetAccuracy: 94,
        length: 210,
        coach: 'Twenty-six letters, eight fingers, no looking down.',
      },
    ],
  },
  /* ────────────────────────────────────────────────────────────── Level 9 */
  {
    slug: 'number-row-typing',
    seoTitle: 'Number Row Typing Lessons for Kids | TypeSwift',
    seoDescription:
      'Learn to type the numbers 0 to 9 by stretching from the home row and returning. Four free typing lessons for kids with an animated keyboard and finger guide.',
    title: 'Number Peaks',
    subtitle: '1 2 3 4 5 6 7 8 9 0',
    emoji: '🔢',
    color: '--k-lagoon',
    newKeys: NUMBERS,
    description:
      'Numbers sit at the very top, the longest reach of all. The trick is to stretch from home and return instantly, without your whole hand drifting upward. Master this and dates, scores and prices stop slowing you down.',
    quickAnswer:
      'The number row is typed by stretching each finger two rows up from the home row and returning immediately. Each finger owns the numbers directly above its home keys — for example the left pointer types 4 and 5.',
    strict: true,
    lessons: [
      {
        title: 'Left hand numbers',
        kind: 'keys',
        focusKeys: ['1', '2', '3', '4', '5'],
        targetWpm: 14,
        targetAccuracy: 88,
        length: 90,
        coach: 'Long reach! Stretch the finger, not the whole hand.',
      },
      {
        title: 'Right hand numbers',
        kind: 'keys',
        focusKeys: ['6', '7', '8', '9', '0'],
        targetWpm: 14,
        targetAccuracy: 88,
        length: 90,
        coach: 'Same idea on the right. Your pinky covers 0.',
      },
      {
        title: 'Numbers in sentences',
        kind: 'sentences',
        focusKeys: [...NUMBERS, '.', ','],
        targetWpm: 20,
        targetAccuracy: 92,
        length: 180,
        coach: 'Mixing letters and numbers is the real skill. Keep the rhythm even.',
      },
      {
        title: 'Number champion',
        kind: 'boss',
        targetWpm: 22,
        targetAccuracy: 93,
        length: 200,
        coach: 'Digits and words together, no peeking.',
      },
    ],
  },
  /* ───────────────────────────────────────────────────────────── Level 10 */
  {
    slug: 'symbols-and-shift',
    seoTitle: 'Shift Key and Symbols — Typing for Kids | TypeSwift',
    seoDescription:
      'Learn capitals and symbols with the opposite-hand Shift rule that keeps both hands anchored. Four free typing lessons for kids with an animated keyboard.',
    title: 'Symbol Sky',
    subtitle: 'Shift, punctuation and symbols',
    emoji: '✨',
    color: '--k-bubblegum',
    newKeys: SYMBOLS,
    description:
      'Capital letters and symbols both need the Shift key, and there is one golden rule: always use the Shift on the opposite side from the letter. Learn that here and your hands stay balanced forever.',
    quickAnswer:
      'To type a capital letter or a symbol, hold the Shift key with the pinky of the hand *opposite* the one pressing the key — left Shift for right-hand letters and right Shift for left-hand letters. This keeps both hands anchored on the home row.',
    strict: true,
    lessons: [
      {
        title: 'Opposite Shift',
        kind: 'keys',
        focusKeys: ['A', 'S', 'K', 'L', 'F', 'J'],
        targetWpm: 16,
        targetAccuracy: 88,
        length: 90,
        coach: 'Left letter? Right Shift. Right letter? Left Shift. Never the same hand.',
      },
      {
        title: 'Everyday punctuation',
        kind: 'keys',
        focusKeys: ['.', ',', '!', '?', "'"],
        targetWpm: 18,
        targetAccuracy: 90,
        length: 100,
        coach: 'These four appear in almost every sentence you will ever type.',
      },
      {
        title: 'Symbols at work',
        kind: 'sentences',
        focusKeys: SYMBOLS,
        targetWpm: 20,
        targetAccuracy: 91,
        length: 190,
        coach: 'Emails, prices and passwords all live here.',
      },
      {
        title: 'Symbol champion',
        kind: 'boss',
        targetWpm: 22,
        targetAccuracy: 93,
        length: 200,
        coach: 'The full keyboard is now under your fingers.',
      },
    ],
  },
  /* ───────────────────────────────────────────────────────────── Level 11 */
  {
    slug: 'typing-sentences',
    seoTitle: 'Typing Full Sentences — Lessons for Kids | TypeSwift',
    seoDescription:
      'Move from single keys to whole sentences with capitals and punctuation. Four free typing lessons for kids that build flow, rhythm and reading ahead.',
    title: 'Sentence Street',
    subtitle: 'Real sentences, capitals and all',
    emoji: '🛣️',
    color: '--k-mint',
    newKeys: [],
    description:
      'From here on the goal changes. You know where every key is — now we build flow. Whole sentences, proper capitals, real punctuation, and the freedom to type through a mistake and fix it after.',
    quickAnswer:
      'Sentence practice teaches typing flow: reading one or two words ahead, keeping an even rhythm through capitals and punctuation, and correcting errors without losing pace.',
    strict: false,
    lessons: [
      {
        title: 'Short sentences',
        kind: 'sentences',
        targetWpm: 22,
        targetAccuracy: 92,
        length: 200,
        coach: 'Read the whole sentence before you start. Then type it in one smooth run.',
      },
      {
        title: 'Longer sentences',
        kind: 'sentences',
        targetWpm: 25,
        targetAccuracy: 93,
        length: 250,
        coach: 'Look one or two words ahead of your fingers — that is the real secret.',
      },
      {
        title: 'Sentence sprint',
        kind: 'timed',
        targetWpm: 26,
        targetAccuracy: 92,
        length: 300,
        durationSec: 60,
        coach: 'Sixty seconds of smooth, steady typing.',
      },
      {
        title: 'Sentence champion',
        kind: 'boss',
        targetWpm: 28,
        targetAccuracy: 94,
        length: 260,
        coach: 'Rhythm beats bursts. Keep it even.',
      },
    ],
  },
  /* ───────────────────────────────────────────────────────────── Level 12 */
  {
    slug: 'typing-paragraphs',
    seoTitle: 'Typing Paragraphs — Stamina Lessons for Kids | TypeSwift',
    seoDescription:
      'Build typing stamina with full paragraphs, the closest practice to real writing and exams. Four free lessons for kids on holding speed and accuracy.',
    title: 'Paragraph Peak',
    subtitle: 'Stamina and focus',
    emoji: '⛰️',
    color: '--k-tangerine',
    newKeys: [],
    description:
      'Speed over ten seconds is easy. Speed over two minutes is a different skill entirely. These paragraphs build the stamina and concentration that real writing, homework and exams demand.',
    quickAnswer:
      'Paragraph practice builds typing stamina — the ability to hold speed and accuracy over several minutes rather than a short burst. It is the closest practice to real writing and exam conditions.',
    strict: false,
    lessons: [
      {
        title: 'Your first paragraph',
        kind: 'paragraph',
        targetWpm: 25,
        targetAccuracy: 93,
        length: 280,
        coach: 'Pace yourself. Finishing cleanly beats starting fast.',
      },
      {
        title: 'Facts and figures',
        kind: 'paragraph',
        targetWpm: 27,
        targetAccuracy: 93,
        length: 320,
        coach: 'Numbers inside prose. Do not let them break your rhythm.',
      },
      {
        title: 'The long haul',
        kind: 'paragraph',
        targetWpm: 28,
        targetAccuracy: 94,
        length: 380,
        coach: 'Shoulders down, wrists floating, eyes forward.',
      },
      {
        title: 'Stamina champion',
        kind: 'boss',
        targetWpm: 30,
        targetAccuracy: 94,
        length: 400,
        coach: 'One long, calm, accurate run.',
      },
    ],
  },
  /* ───────────────────────────────────────────────────────────── Level 13 */
  {
    slug: 'typing-stories',
    seoTitle: 'Story Typing — Fun Typing Lessons for Kids | TypeSwift',
    seoDescription:
      'Type three short illustrated stories from start to finish. Free typing lessons for kids that make typing automatic by putting the attention on the story.',
    title: 'Story Forest',
    subtitle: 'Type a whole story',
    emoji: '🌲',
    color: '--k-mint',
    newKeys: [],
    description:
      'Three short stories, typed one part at a time. Because you actually want to know what happens next, you stop thinking about your fingers — and that is exactly when typing becomes automatic.',
    quickAnswer:
      "Story typing uses narrative to shift a learner's attention away from the keyboard and onto meaning, which is the point at which typing becomes automatic rather than deliberate.",
    strict: false,
    lessons: [
      {
        title: 'The Lighthouse Cat',
        kind: 'story',
        targetWpm: 28,
        targetAccuracy: 93,
        length: 420,
        coach: 'A stormy night, a jammed lamp and one very determined cat.',
      },
      {
        title: 'The Seed That Waited',
        kind: 'story',
        targetWpm: 30,
        targetAccuracy: 93,
        length: 400,
        coach: 'Small things become big things. So does practice.',
      },
      {
        title: 'Bolt Learns to Type',
        kind: 'story',
        targetWpm: 32,
        targetAccuracy: 94,
        length: 430,
        coach: 'A robot with ten fingers and no idea what to do with them.',
      },
    ],
  },
  /* ───────────────────────────────────────────────────────────── Level 14 */
  {
    slug: 'timed-typing-challenges',
    seoTitle: 'Timed Typing Challenges for Kids | TypeSwift',
    seoDescription:
      'Beat the clock with 30-second, one-minute and two-minute typing challenges. Four free timed lessons for kids that build composure as well as speed.',
    title: 'Beat the Clock',
    subtitle: 'Timed challenges',
    emoji: '⏱️',
    color: '--k-melon',
    newKeys: [],
    description:
      'Now we add pressure — on purpose. Short timed runs teach you to stay calm and accurate when the clock is ticking, which is exactly what a typing test or an exam feels like.',
    quickAnswer:
      'Timed typing challenges measure how many words per minute a typist can sustain under time pressure. Practising against a clock builds composure as well as speed.',
    strict: false,
    lessons: [
      {
        title: '30 second dash',
        kind: 'timed',
        targetWpm: 30,
        targetAccuracy: 93,
        length: 200,
        durationSec: 30,
        coach: 'Short and sharp. Do not panic — just type normally, faster.',
      },
      {
        title: '60 second run',
        kind: 'timed',
        targetWpm: 32,
        targetAccuracy: 93,
        length: 400,
        durationSec: 60,
        coach: 'A full minute. Settle into a rhythm in the first five seconds.',
      },
      {
        title: '2 minute endurance',
        kind: 'timed',
        targetWpm: 34,
        targetAccuracy: 94,
        length: 800,
        durationSec: 120,
        coach: 'Two minutes. Breathe. Let the pace carry you.',
      },
      {
        title: 'Clock champion',
        kind: 'boss',
        targetWpm: 36,
        targetAccuracy: 95,
        length: 420,
        durationSec: 60,
        coach: 'Speed and accuracy at the same time. You can do both.',
      },
    ],
  },
  /* ───────────────────────────────────────────────────────────── Level 15 */
  {
    slug: 'master-typist',
    seoTitle: 'Master Typist Trials — Typing for Kids | TypeSwift',
    seoDescription:
      'The final four trials: accuracy, speed, everything combined, and the timed finale. Complete them to earn the free printable Master Typist certificate.',
    title: 'Master Typist',
    subtitle: 'The final trials',
    emoji: '👑',
    color: '--k-mango',
    newKeys: [],
    description:
      'The last level. Everything you have learned, all at once: full alphabet, numbers, symbols, capitals, long passages and the clock. Clear all four trials and you have earned the Master Typist certificate.',
    quickAnswer:
      'The Master Typist level combines letters, numbers, symbols and timed passages into four final trials. Completing all of them at 40 words per minute with 95% accuracy earns the course certificate.',
    strict: false,
    lessons: [
      {
        title: 'Trial of Accuracy',
        kind: 'paragraph',
        targetWpm: 32,
        targetAccuracy: 97,
        length: 400,
        coach: 'Accuracy is the trial here. Ninety-seven percent or better.',
      },
      {
        title: 'Trial of Speed',
        kind: 'timed',
        targetWpm: 40,
        targetAccuracy: 93,
        length: 500,
        durationSec: 60,
        coach: 'Let go. Type as fast as you honestly can.',
      },
      {
        title: 'Trial of Everything',
        kind: 'sentences',
        targetWpm: 36,
        targetAccuracy: 95,
        length: 420,
        coach: 'Letters, numbers, symbols, capitals. All of it.',
      },
      {
        title: 'The Final Trial',
        kind: 'boss',
        targetWpm: 40,
        targetAccuracy: 95,
        length: 520,
        durationSec: 120,
        coach: 'This is it. Two minutes to become a Master Typist.',
      },
    ],
  },
];

/* ────────────────────────────────────────────────── build the level graph ── */

const ALPHABET_AFTER: Record<number, string[]> = {};

export const LEVELS: LevelDef[] = (() => {
  const out: LevelDef[] = [];
  let cumulative: string[] = [];

  LEVEL_SEEDS.forEach((seed, i) => {
    const id = `l${String(i + 1).padStart(2, '0')}`;
    cumulative = Array.from(new Set([...cumulative, ...seed.newKeys]));
    ALPHABET_AFTER[i + 1] = cumulative;

    const lessons: LessonDef[] = seed.lessons.map((ls, j) => ({
      id: `${id}-${j + 1}`,
      levelId: id,
      index: j + 1,
      title: ls.title,
      kind: ls.kind,
      focusKeys: ls.focusKeys ?? (seed.newKeys.length ? seed.newKeys : cumulative),
      targetWpm: ls.targetWpm,
      targetAccuracy: ls.targetAccuracy,
      length: ls.length,
      durationSec: ls.durationSec,
      strict: ls.strict ?? seed.strict,
      coach: ls.coach,
    }));

    out.push({
      id,
      slug: seed.slug,
      seoTitle: seed.seoTitle,
      seoDescription: seed.seoDescription,
      index: i + 1,
      title: seed.title,
      subtitle: seed.subtitle,
      emoji: seed.emoji,
      color: seed.color,
      newKeys: seed.newKeys,
      cumulativeKeys: cumulative.slice(),
      description: seed.description,
      quickAnswer: seed.quickAnswer,
      lessons,
    });
  });

  return out;
})();

export const ALL_LESSONS: LessonDef[] = LEVELS.flatMap((l) => l.lessons);

export const LESSON_COUNT = ALL_LESSONS.length;
export const LEVEL_COUNT = LEVELS.length;

const LEVEL_BY_ID = new Map(LEVELS.map((l) => [l.id, l]));
const LEVEL_BY_SLUG = new Map(LEVELS.map((l) => [l.slug, l]));
const LESSON_BY_ID = new Map(ALL_LESSONS.map((l) => [l.id, l]));

export const getLevel = (id: string) => LEVEL_BY_ID.get(id) ?? null;
export const getLevelBySlug = (slug: string) => LEVEL_BY_SLUG.get(slug) ?? null;
export const getLesson = (id: string) => LESSON_BY_ID.get(id) ?? null;

export function getLevelOfLesson(lessonId: string): LevelDef | null {
  const lesson = getLesson(lessonId);
  return lesson ? getLevel(lesson.levelId) : null;
}

/** The next lesson in course order, or null at the end of the journey. */
export function nextLesson(lessonId: string): LessonDef | null {
  const i = ALL_LESSONS.findIndex((l) => l.id === lessonId);
  return i >= 0 && i < ALL_LESSONS.length - 1 ? ALL_LESSONS[i + 1] : null;
}

export function previousLesson(lessonId: string): LessonDef | null {
  const i = ALL_LESSONS.findIndex((l) => l.id === lessonId);
  return i > 0 ? ALL_LESSONS[i - 1] : null;
}

/** Every key the child can legally be shown by the end of `levelIndex`. */
export function keysAvailableAt(levelIndex: number): string[] {
  return ALPHABET_AFTER[Math.max(1, Math.min(levelIndex, LEVEL_COUNT))] ?? [];
}
